import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { Button, PillButton } from '@mobi/component-library/Base/Buttons'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { ToastContext } from '@mobi/component-library/Feedback/Toast/context'
import type { ToastContent, ToastItem } from '@mobi/component-library/src/Feedback/Toast/types'
import { Icons } from '@mobi/component-library/Assets/Icon/library'

/**
 * Some icons should be bundled with the app, rather than lazy loaded. There are two
 * reasons to pre-load icons for the toast:
 * 1. Minimise delay in rendering, and ensuring smooth animation
 * 2. Prevent white screen (i.e if no error boundary), or missing icons if the app is offline; keeping in mind
 *    that toasts are often used to report errors, including connectivity issues
 */
Icons['LineInfoCircle'].preload()
Icons['LineAlertTriangle'].preload()
Icons['LineAlertCircle'].preload()
Icons['SolidXClose'].preload()

const enum LocalConstants {
  LightThemeClass = 'js-toast--light-theme',
  DarkThemeClass = 'js-toast--dark-theme',
  NoRightAlignedClass = 'js-toast__btns--none',
}

export const Content: React.FC<ToastItem> = ({
  id,
  icon,
  title,
  subtitle,
  button,
  theme,
  isDismissable,
}) => {
  const toastEmitter = React.useContext(ToastContext).toastEmitter

  const classNames = [
    theme === 'dark' ? LocalConstants.DarkThemeClass : LocalConstants.LightThemeClass,
  ]
  if (!button && !isDismissable) classNames.push(LocalConstants.NoRightAlignedClass)

  return (
    <WrapperStyled className={classNames.join(' ')}>
      <div data-icon-type={icon}>
        {icon && <Icon color='inherit' name={iconPathDict[icon]} size='2rem' />}
      </div>

      <div>
        {title && (
          <>
            <span>{title}</span>
            {subtitle && <span>{subtitle}</span>}
          </>
        )}
      </div>

      <div>
        {button && (
          <Button
            size='xs'
            onClick={button.onClick}
            color={theme === 'dark' ? 'secondary_dark' : 'secondary_color'}
          >
            {button.text}
          </Button>
        )}

        {isDismissable && (
          <PillButton
            size='xs'
            type='button'
            isIconOnlyButton
            color={theme === 'dark' ? 'tertiary_dark' : 'tertiary_grey'}
            aria-label='Dismiss toast'
            onClick={() => toastEmitter.emit('removeToast', { id })}
          >
            <Icon name='SolidXClose' size='1.6rem' color='inherit' />
          </PillButton>
        )}
      </div>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const iconPathDict: Record<
  NonNullable<ToastContent['icon']>,
  React.ComponentProps<typeof Icon>['name']
> = {
  success: 'LineCheckCircle',
  warning: 'LineAlertTriangle',
  error: 'LineAlertCircle',
  info: 'LineInfoCircle',
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: spacing.smx1,
  minHeight: '4.8rem',
  padding: spacing.smx1,
  paddingLeft: spacing.md,
  borderRadius: radius.lgx2,

  fontFamily: font.family.primary,
  fontSize: font.size.lg.fontSize,
  letterSpacing: font.size.lg.letterSpacing,
  lineHeight: font.size.lg.lineHeight,

  // Icon
  '> div:nth-of-type(1)': {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',

    '&[data-icon-type="success"]': { color: colors.success[400] },
    '&[data-icon-type="warning"]': { color: colors.warning[500] },
    '&[data-icon-type="error"]': { color: colors.error[500] },
    '&[data-icon-type="info"]': { color: colors.mariner[600] },
  },

  // Titles
  '> div:nth-of-type(2)': {
    display: 'flex',
    flexDirection: 'column',

    '> span:nth-of-type(1)': { fontWeight: font.weight.medium },
    '> span:nth-of-type(2)': { fontWeight: font.weight.regular },
  },

  // CTA Button & Close button
  '> div:nth-of-type(3)': {
    display: 'flex',
    gap: spacing.smx2,
    marginLeft: 'auto',
  },

  ['&.' + LocalConstants.DarkThemeClass]: {
    boxShadow: '0 4px 8px -2px rgba(26, 14, 35, 0.45), 0 2px 4px -2px rgba(26, 14, 35, 0.06)',
    backgroundColor: colors.lavender[900],

    '> div:nth-of-type(2)': {
      '> span:nth-of-type(1)': { color: colors.white },
      '> span:nth-of-type(2)': { color: colors.lavender[50] },
    },
  },

  ['&.' + LocalConstants.LightThemeClass]: {
    boxShadow: shadow.md,
    border: `1px solid ${colors.neutral[200]}`,
    backgroundColor: colors.white,

    '> div:nth-of-type(2)': {
      '> span:nth-of-type(1)': { color: colors.black },
      '> span:nth-of-type(2)': { color: colors.lavender[700] },
    },
  },

  ['&.' + LocalConstants.NoRightAlignedClass]: {
    paddingRight: spacing.md,
  },

  '> div:empty': { display: 'none' },
})
